'use client';

import React, { useMemo } from 'react';
import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from 'recharts';

import { ChartContainer, type ChartConfig } from '@repo/ui/components/ui/chart';

import { useResponsiveRadialChartDimensions } from '@/lib/chart-utils';
import { usePlatform } from '@/hooks/use-platform';
import { useAnalysisContainer } from '@/components/article-analysis/analysis-container';

interface BiasChartProps {
  biasScore?: number;
}

export const BiasChart: React.FC<BiasChartProps> = ({ biasScore }) => {
  const { isWebkit } = usePlatform();
  const chartDimensions = useResponsiveRadialChartDimensions(
    'large',
    useAnalysisContainer(),
  );

  const biasScoreChart = {
    chartData: [
      {
        type: 'bias',
        score: Number.isFinite(biasScore) ? Math.round(biasScore || 0) : 0,
        fill: 'hsl(var(--facts-pie-chart))',
      },
    ],
    chartConfig: {
      score: {
        label: 'Score',
      },
      bias: {
        label: 'Bias',
        color: 'hsl(var(--facts-pie-chart))',
      },
    } satisfies ChartConfig,
  };

  const endAngle = useMemo(() => {
    return biasScore ? 90 + biasScore * 3.6 : 90;
  }, [biasScore]);

  const labelsY = useMemo(() => {
    if (chartDimensions.innerRadius >= 57) {
      return {
        top: 10,
        bottom: 13,
      };
    }
    if (chartDimensions.innerRadius >= 43) {
      return {
        top: 8,
        bottom: 11,
      };
    }
    if (chartDimensions.innerRadius >= 40) {
      return {
        top: 6,
        bottom: 10,
      };
    }
    return {
      top: 6,
      bottom: 10,
    };
  }, [chartDimensions.innerRadius]);

  return (
    <ChartContainer
      config={biasScoreChart.chartConfig}
      className="aspect-square max-h-20 w-full @cred-md:max-h-22.75 @cred-lg:max-h-24.75 @cred-xl:max-h-32.5 [&_.recharts-radial-bar-background-sector]:fill-background"
    >
      <RadialBarChart
        data={biasScoreChart.chartData}
        startAngle={90}
        endAngle={endAngle}
        innerRadius={chartDimensions.innerRadius}
        outerRadius={chartDimensions.outerRadius}
      >
        <PolarGrid
          gridType="circle"
          radialLines={false}
          stroke="none"
          className="first:fill-[#333333] last:fill-background"
          polarRadius={chartDimensions.polarRadius}
        />
        <RadialBar dataKey="score" background cornerRadius={10} />
        <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
          <Label
            content={({ viewBox }) => {
              if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={
                        isWebkit
                          ? (viewBox.cy || 0) - 1
                          : (viewBox.cy || 0) - labelsY.top
                      }
                      className="fill-neutral-1 text-lg font-bold leading-5.25 @cred-md:text-xl @cred-md:leading-5.75 @cred-lg:text-[22px] @cred-lg:leading-6.5 @cred-xl:text-[30px] @cred-xl:leading-8.75"
                    >
                      {biasScoreChart.chartData[0]?.score + '%'}
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={
                        isWebkit
                          ? (viewBox.cy || 0) + labelsY.bottom * 1.35
                          : (viewBox.cy || 0) + labelsY.bottom
                      }
                      className="fill-neutral-1 text-xs font-bold leading-3.5 @cred-md:text-sm @cred-md:leading-4 @cred-lg:text-base @cred-lg:leading-4.75 @cred-xl:text-xl @cred-xl:leading-5.75"
                    >
                      Bias
                    </tspan>
                  </text>
                );
              }
            }}
          />
        </PolarRadiusAxis>
      </RadialBarChart>
    </ChartContainer>
  );
};
